import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewEncapsulation,
  effect,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { SelectCompany } from '@app/features/companies/select-company/select-company';
import { ContractService } from '@app/features/contracts/contract-service';
import {
  Contract,
  ContractListItem,
  ContractType,
  ContractTypes,
} from '@app/features/contracts/contract.model';
import { SelectContractType } from '@app/features/contracts/select-contract-type/select-contract-type';
import { SelectMostRecentContract } from '@app/features/contracts/select-most-recent-contract/select-most-recent-contract';
import { SelectReplacementReason } from '@app/features/contracts/select-replacement-reason/select-replacement-reason';
import { PersonTitle, PersonTitles } from '@app/features/employees/employee.model';
import { SelectJobType } from '@app/features/job-types/select-job-type/select-job-type';
import { SelectQualificationLevel } from '@app/features/job-types/select-qualification-level/select-qualification-level';
import { SalaryScale, SalaryScaleContractUpdate } from '@app/features/salary/salary-scale.model';
import { SalaryScalesService } from '@app/features/salary/salary-scales-service';
import { ControlsOf } from '@app/shared/forms/controls-of.form-group';
import { dateRangeValidator } from '@app/shared/forms/date-range-validator/date-range-validator';
import { LabelValue } from '@app/shared/forms/label-value/label-value';
import { TitlecaseDirective } from '@app/shared/pipes/titlecase.directive';
import { ToUppercaseDirective } from '@app/shared/pipes/to-uppercase.directive';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import { WarningTipsComponent } from '@app/shared/tips/warn-tips/warn-tips.component';
import diff from 'microdiff';
import moment from 'moment';
import { Subscription, map, merge, startWith } from 'rxjs';

export interface ContractFormValue {
  id: string | null;
  companyId: string;
  contractType: ContractType | null;
  startDate: string | null;
  endDate: string | null;
  endOfFirstTrialPeriodDate: string | null;
  replacement: ReplacementFormValue | null;
  jobId: number | null;
  qualificationLevel: string | null;
  grossHourlySalary: number;
  companyCar: boolean;
  previousContractId: string | null;
  /** Ouvert comme une prolongation sans interruption : le contrat précédent est à choisir. */
  renewal?: boolean;
}

export interface ReplacementFormValue {
  civility: PersonTitle | null;
  firstName: string | null;
  lastName: string | null;
  reason: string | null;
  jobId: number | null;
  qualificationLevel: string | null;
}

@Component({
  selector: 'app-contract-form',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    SelectContractType,
    MatDatepickerModule,
    SelectCompany,
    SelectReplacementReason,
    SelectJobType,
    SelectQualificationLevel,
    CurrencyPipe,
    LabelValue,
    MatButtonModule,
    MatTooltip,
    MatRadioModule,
    MatCheckbox,
    DatePipe,
    ToUppercaseDirective,
    TitlecaseDirective,
    SelectMostRecentContract,
    WarningTipsComponent,
  ],
  templateUrl: './contract-form.html',
  styleUrl: './contract-form.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class ContractForm extends RxjsDirective implements OnInit {
  ContractTypes = ContractTypes;
  PersonTitles = PersonTitles;

  cdr = inject(ChangeDetectorRef);
  initWith = input<ContractFormValue>();
  lastSalaryScaleContractUpdate = input<SalaryScaleContractUpdate>();
  employeeId = input<string | null | undefined>(null);
  contractService = inject(ContractService);

  validation = input<boolean>(false);
  readOnly = input<boolean>(false);
  previousContractSelected = output<Contract>();

  private readonly salaryScaleService = inject(SalaryScalesService);
  private initialValues?: ContractFormValue;
  salaryScale: SalaryScale | null = null;
  minGrossHourlySalary = 0;
  grossHourlySalaryUpdated = false;
  private grossHourlySalaryUpdatedTimeout?: ReturnType<typeof setTimeout>;
  overlappingContracts: ContractListItem[] = [];

  showDateDeFinForContractTypes: ContractType[] = ['CDD R', 'CDD RST', 'CDD A'];

  companyId = new FormControl('', { nonNullable: true });
  replacementForm = new FormGroup<ControlsOf<ReplacementFormValue>>({
    civility: new FormControl<PersonTitle | null>(null),
    firstName: new FormControl<string | null>(null),
    lastName: new FormControl<string | null>(null),
    reason: new FormControl<string | null>(null),
    jobId: new FormControl<number | null>(null),
    qualificationLevel: new FormControl<string | null>(null),
  });
  renewalControl = new FormControl<boolean>(false, { nonNullable: true });
  contractForm = new FormGroup(
    {
      id: new FormControl<string | null>(null),
      contractType: new FormControl<ContractType | null>(null, {
        nonNullable: true,
      }),
      startDate: new FormControl<string | null>(null, { nonNullable: true }),
      endDate: new FormControl<string | null>(null),
      endOfFirstTrialPeriodDate: new FormControl<string | null>(null),
      jobId: new FormControl<number | null>(null),
      qualificationLevel: new FormControl<string | null>(null),
      grossHourlySalary: new FormControl<number>(0, {
        nonNullable: true,
        validators: [Validators.min(0)],
      }),
      companyCar: new FormControl<boolean>(false, { nonNullable: true }),
      previousContractId: new FormControl<string | null>(null),
    },
    {
      validators: [
        dateRangeValidator('startDate', 'endDate', 'startEndDateRange'),
        dateRangeValidator(
          'startDate',
          'endOfFirstTrialPeriodDate',
          'startEndOfFirstTrialPeriodDateRange',
        ),
      ],
    },
  );

  readonly valueSig = toSignal(
    merge(
      this.companyId.valueChanges,
      this.contractForm.valueChanges,
      this.replacementForm.valueChanges,
    ).pipe(
      startWith(null),
      map(() => ({
        companyId: this.companyId.value,
        replacement: this.replacementForm.getRawValue(),
        ...this.contractForm.getRawValue(),
      })),
    ),
    {
      initialValue: {
        companyId: this.companyId.value,
        replacement: this.replacementForm.getRawValue(),
        ...this.contractForm.getRawValue(),
      },
    },
  );

  constructor() {
    super();
    effect(() => this.valueSig());
    effect(() => {
      this.applyReadOnlyState(this.readOnly());
    });
    effect(() => {
      if (this.validation()) {
        this.contractForm.markAllAsTouched();
        this.companyId.markAsTouched();
        this.replacementForm.markAllAsTouched();
      }
    });
    effect(() => {
      this.employeeId();
      this.fetchOverlappingContracts();
    });
  }

  ngOnInit(): void {
    const initWith = this.initWith();
    this.renewalControl.valueChanges.subscribe((renewal) => {
      if (!renewal) {
        this.contractForm.controls.previousContractId.setValue(null);
        this.companyId.enable({ emitEvent: false });
      }
    });
    if (initWith) {
      this.initialValues = structuredClone(initWith);
      const { replacement } = this.initialValues;
      const hasReplacement = Object.keys(replacement || {}).some(
        (key) => replacement![key as keyof ReplacementFormValue] !== null,
      );
      if (replacement && hasReplacement) {
        this.replacementForm.patchValue(replacement);
      }
      this.contractForm.patchValue(this.initialValues);
      this.companyId.setValue(this.initialValues.companyId);
      this.renewalControl.setValue(
        this.initialValues.previousContractId !== null || this.initialValues.renewal === true,
      );
      this.updateTrialPeriodEnability();
      this.ensureSalaryScaleUpToDateAndRefreshMinSalary();
    }

    this.updateQualificationLevelEnabledState();

    this.register(
      this.contractForm.controls.jobId.valueChanges.subscribe(() => {
        this.updateQualificationLevelEnabledState();
      }),
      this.contractForm.controls.startDate.valueChanges.subscribe(() => {
        this.onContractStartDateChanged();
        this.calculateEndOfFirstTrialPeriodDate();
        this.fetchOverlappingContracts();
      }),
      this.contractForm.controls.endDate.valueChanges.subscribe(() => {
        this.calculateEndOfFirstTrialPeriodDate();
        this.fetchOverlappingContracts();
      }),
      this.contractForm.controls.contractType.valueChanges.subscribe(() => {
        this.fetchOverlappingContracts();
      }),
      this.contractForm.controls.qualificationLevel.valueChanges.subscribe((change) => {
        console.log('qualificationLevel changed', change);
      }),
    );
  }

  updateQualificationLevelEnabledState() {
    const jobId = this.contractForm.controls.jobId.value;
    if (this.readOnly()) {
      this.contractForm.controls.qualificationLevel.disable({ emitEvent: false });
      return;
    }

    if (!jobId) {
      this.contractForm.controls.qualificationLevel.disable({ emitEvent: false });
    } else {
      this.contractForm.controls.qualificationLevel.enable({ emitEvent: false });
    }
  }

  onPreviousContractSelected($event: string | null) {
    this.contractForm.controls.previousContractId.setValue($event);
    if ($event) {
      this.companyId.disable({ emitEvent: false });
    } else {
      this.companyId.enable({ emitEvent: false });
      return;
    }
    this.register(
      this.contractService.findOne($event!).subscribe((contract) => {
        const contractPart = contract.contract;
        this.companyId.setValue(contract.company.id ?? '');
        this.contractForm.patchValue({
          contractType: contractPart.contractType,
          jobId: contractPart.jobType?.id ?? null,
          qualificationLevel: contractPart.qualificationLevel,
        });
        this.onContractTypeChange(contractPart.contractType!);
        this.ensureSalaryScaleUpToDateAndRefreshMinSalary();
        this.previousContractSelected.emit(contract);
      }),
    );
  }

  onContractTypeChange(contractType: ContractType) {
    this.updateTrialPeriodEnability();
    this.updateReplacementFormGroupState(contractType);
  }

  get isReplacement(): boolean {
    return (
      this.contractForm.controls.contractType.value === 'CDD R' ||
      this.contractForm.controls.contractType.value === 'CDD RST'
    );
  }

  get updatedBySalaryScale(): boolean {
    return (
      this.contractForm.controls.grossHourlySalary.value ==
      this.lastSalaryScaleContractUpdate()?.newGrossHourlySalary
    );
  }

  private updateReplacementFormGroupState(contractType: string) {
    if (this.isReplacement) {
      this.replacementForm.setValue({
        civility: 'Monsieur',
        firstName: null,
        lastName: null,
        reason: null,
        jobId: null,
        qualificationLevel: null,
      });
    } else {
      this.replacementForm.reset();
    }
    this.replacementForm.updateValueAndValidity({ emitEvent: false });
  }

  get isValid(): boolean {
    return (this.companyId.disabled || this.companyId.valid) && this.contractForm.valid;
  }

  get isDirty(): boolean {
    if (!this.initialValues) return false;
    const current: ContractFormValue = {
      companyId: this.companyId.value,
      ...this.contractForm.getRawValue(),
      replacement: this.replacementForm.getRawValue(),
    };
    const modifidKeys = diff(this.initialValues, current);
    return modifidKeys.length > 0;
  }

  get showDateDeFin(): boolean {
    if (!this.contractForm.controls.contractType.value) return false;
    return this.showDateDeFinForContractTypes.includes(
      this.contractForm.controls.contractType.value,
    );
  }

  get contractDuration(): string {
    if (this.contractForm.controls.endDate.value && this.contractForm.controls.startDate.value) {
      const start = moment(this.contractForm.controls.startDate.value);
      const end = moment(this.contractForm.controls.endDate.value);
      const diffInDays = end.diff(start, 'days') + 1;
      const diffInWeeks = end.add(1, 'days').diff(start, 'weeks');
      return `${diffInWeeks} semaines (${diffInDays} jours)`;
    }
    return '';
  }

  get trialPeriodDurationInDays(): number | null {
    if (
      this.contractForm.controls.endOfFirstTrialPeriodDate.value &&
      this.contractForm.controls.startDate.value
    ) {
      const start = moment(this.contractForm.controls.startDate.value).startOf('day');
      const end = moment(this.contractForm.controls.endOfFirstTrialPeriodDate.value).endOf('day');
      const duration = end.diff(start, 'days') + 1; // +1 to include the end date in the duration
      return duration;
    }
    return null;
  }

  getModifiedFields(): Record<string, { current: unknown; initial: unknown }> {
    if (!this.initialValues) return {};
    const current: ContractFormValue = {
      companyId: this.companyId.value,
      ...this.contractForm.getRawValue(),
      replacement: this.replacementForm.getRawValue(),
    };
    const modified: Record<string, { current: unknown; initial: unknown }> = {};
    Object.keys(this.initialValues).forEach((key) => {
      if (
        current[key as keyof ContractFormValue] !==
        this.initialValues![key as keyof ContractFormValue]
      ) {
        modified[key] = {
          current: current[key as keyof ContractFormValue],
          initial: this.initialValues![key as keyof ContractFormValue],
        };
      }
    });
    return modified;
  }

  ensureSalaryScaleUpToDateAndRefreshMinSalary() {
    if (this.readOnly()) {
      return;
    }
    if (
      this.contractForm.controls.jobId.value === 0 ||
      !this.contractForm.controls.startDate.value
    ) {
      this.salaryScale = null;
      this.updateGrossHourlySalary();
      return;
    } else {
      this.salaryScaleService
        .findMostRecentBefore(
          this.contractForm.controls.jobId.value!,
          this.contractForm.controls.startDate.value!,
        )
        .subscribe((salaryScale) => {
          this.salaryScale = salaryScale;
          this.updateGrossHourlySalary();
        });
    }
  }

  private overlappingContractsSubscription: Subscription | null = null;

  private fetchOverlappingContracts(): void {
    if (!this.employeeId()) {
      this.overlappingContracts = [];
      this.overlappingContractsSubscription?.unsubscribe();
      this.overlappingContractsSubscription = null;
      return;
    }

    const startDate = this.contractForm.controls.startDate.value;
    const endDate = this.contractForm.controls.endDate.value;

    if (!startDate || !endDate) {
      this.overlappingContracts = [];
      this.overlappingContractsSubscription?.unsubscribe();
      this.overlappingContractsSubscription = null;
      return;
    }

    this.overlappingContractsSubscription?.unsubscribe();
    this.overlappingContractsSubscription = this.contractService
      .mostRecentsByEmployee(this.employeeId()!, this.contractForm.controls.id.value)
      .subscribe((contracts) => {
        const overlapping = contracts.filter((contract) =>
          this.periodsOverlap(startDate, endDate, contract.startDate, contract.endDate),
        );
        Promise.resolve().then(() => {
          this.overlappingContracts = overlapping;
          this.cdr.markForCheck();
        });
      });
  }

  private periodsOverlap(
    startDate: string,
    endDate: string,
    otherStartDate: string | null,
    otherEndDate: string | null,
  ): boolean {
    if (!otherStartDate) {
      return false;
    }
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).endOf('day');
    const otherStart = moment(otherStartDate).startOf('day');
    const otherEnd = otherEndDate
      ? moment(otherEndDate).endOf('day')
      : moment('9999-12-31').endOf('day');
    return start.isSameOrBefore(otherEnd) && otherStart.isSameOrBefore(end);
  }

  get calculateEndOfFirstTrialPeriodDateEnabled(): boolean {
    return (
      this.contractForm.controls.startDate.valid &&
      this.contractForm.controls.contractType.value !== undefined &&
      this.contractForm.controls.endOfFirstTrialPeriodDate.enabled
    );
  }

  calculateEndOfFirstTrialPeriodDate() {
    if (
      !this.calculateEndOfFirstTrialPeriodDateEnabled ||
      !this.contractForm.controls.startDate.value
    ) {
      return;
    }
    const contractType = this.contractForm.controls.contractType.value;
    // moment mutates the original date, so we create a function to get a new moment object each time
    const startDateMoment = () =>
      moment(this.contractForm.controls.startDate.value!).startOf('day');
    let endOfFirstTrialPeriodDate: string | null = this.contractForm.controls.startDate.value!;
    switch (contractType) {
      case 'CDI':
        endOfFirstTrialPeriodDate = startDateMoment()
          .add(1, 'months')
          .add(-1, 'days')
          .format('YYYY-MM-DD');
        break;
      case 'CDD RST': {
        // 1j/sem jusqu'à 2 semaines
        const contractDurationInWeeks = moment(this.contractForm.controls.endDate.value!).diff(
          startDateMoment().subtract(1, 'days'),
          'weeks',
        );
        // si la durée du contrat n'est pas définie ou est inférieure à 1 semaine, on ne met pas de période d'essai
        if (!contractDurationInWeeks || contractDurationInWeeks <= 0) {
          // Null représente l'absence de période d'essai,
          endOfFirstTrialPeriodDate = null;
          break;
        }
        const trialPeriodDurationInDays = Math.min(14, contractDurationInWeeks);
        endOfFirstTrialPeriodDate = startDateMoment()
          .add(trialPeriodDurationInDays - 1, 'days')
          .format('YYYY-MM-DD');
        break;
      }
      case 'CDD R':
      case 'CDD A': {
        // On retire 1 jour pour que la date de fin de contrat soit incluse dans le calcul
        const sixMonthsLater = startDateMoment().add(6, 'months').add(-1, 'days');
        const contractDurationInWeeks = moment(this.contractForm.controls.endDate.value!).diff(
          // moment calcule le nombre de jours à parcourir entre les deux dates,
          // donc on retire 1 jour pour que la date de fin de contrat soit incluse dans le calcule
          startDateMoment().subtract(1, 'days'),
          'weeks',
        );
        // si la durée du contrat n'est pas définie ou est inférieure à 1 semaine, on ne met pas de période d'essai
        if (!contractDurationInWeeks || contractDurationInWeeks <= 0) {
          // Null représente l'absence de période d'essai,
          endOfFirstTrialPeriodDate = null;
          break;
        }
        const contractEndDateMoment = moment(this.contractForm.controls.endDate.value!);
        if (contractEndDateMoment.isSameOrBefore(sixMonthsLater)) {
          // <= 6 mois alors 1j/sem (max 2 semaines)
          endOfFirstTrialPeriodDate = startDateMoment()
            // On retire 1 jour pour faire correspondre à la fin de la journée du dernier jour de période d'essai
            .add(Math.min(14, contractDurationInWeeks) - 1, 'days')
            .format('YYYY-MM-DD');
        } else {
          // > 6 mois alors 1j/sem (max 1 mois)
          const oneMonthLater = startDateMoment().add(1, 'months').add(-1, 'days');
          endOfFirstTrialPeriodDate = moment
            .min(
              oneMonthLater,
              startDateMoment()
                // On retire 1 jour pour faire correspondre à la fin de la journée du dernier jour de période d'essai
                .add(contractDurationInWeeks - 1, 'days'),
            )
            .format('YYYY-MM-DD');
        }
        break;
      }
    }
    this.contractForm.controls.endOfFirstTrialPeriodDate.setValue(endOfFirstTrialPeriodDate);
  }

  private onContractStartDateChanged(): void {
    const startDate = this.contractForm.controls.startDate.value;
    if (!startDate) {
      return;
    }

    const previousContractId = this.contractForm.controls.previousContractId.value;
    if (!previousContractId) {
      return;
    }

    this.register(
      this.contractService.findOne(previousContractId).subscribe((contract) => {
        const previousStartDate = contract.contract?.startDate;
        if (!previousStartDate || !moment(previousStartDate).isBefore(startDate, 'day')) {
          this.contractForm.controls.previousContractId.setValue(null);
          this.companyId.enable({ emitEvent: false });
        }
      }),
    );
  }

  updateTrialPeriodEnability() {
    if (this.readOnly()) {
      this.contractForm.controls.endOfFirstTrialPeriodDate.disable({ emitEvent: false });
      return;
    }
    const hasTrialPeriod = !this.renewalControl.value;
    if (hasTrialPeriod) {
      this.contractForm.controls.endOfFirstTrialPeriodDate.enable({ emitEvent: false });
      this.calculateEndOfFirstTrialPeriodDate();
    } else {
      this.contractForm.controls.endOfFirstTrialPeriodDate.disable({ emitEvent: false });
      this.contractForm.controls.endOfFirstTrialPeriodDate.setValue('');
    }
  }

  updateGrossHourlySalary() {
    const qualificationLevel = this.contractForm.controls.qualificationLevel.value;

    // Récupération du salaire minimum pour la qualification sélectionnée
    if (!qualificationLevel || !this.salaryScale) {
      this.minGrossHourlySalary = 0;
    } else {
      const salaryEntry = this.salaryScale.entries.find(
        (entry) => entry.qualificationLevel === qualificationLevel,
      );
      this.minGrossHourlySalary = salaryEntry ? salaryEntry.grossHourlySalary : 0;
    }
    // MAJ du champ de saisie du salaire horaire brut avec le salaire minimum de la qualification sélectionnée
    const previousSalary = this.contractForm.controls.grossHourlySalary.value;
    const newSalary = this.minGrossHourlySalary;

    this.contractForm.controls.grossHourlySalary.setValue(newSalary);

    if (previousSalary !== newSalary) {
      this.grossHourlySalaryUpdated = true;
      clearTimeout(this.grossHourlySalaryUpdatedTimeout);
      this.grossHourlySalaryUpdatedTimeout = setTimeout(() => {
        this.grossHourlySalaryUpdated = false;
      }, 1500);
    }

    this.updateGrossHourlySalaryValidator();
  }

  private updateGrossHourlySalaryValidator() {
    this.contractForm.controls.grossHourlySalary.setValidators([
      Validators.required,
      Validators.min(this.minGrossHourlySalary),
    ]);
    this.contractForm.controls.grossHourlySalary.updateValueAndValidity();
  }

  private applyReadOnlyState(readOnly: boolean): void {
    if (readOnly) {
      this.companyId.disable({ emitEvent: false });
      this.contractForm.disable({ emitEvent: false });
      this.replacementForm.disable({ emitEvent: false });
      return;
    }

    this.companyId.enable({ emitEvent: false });
    this.contractForm.enable({ emitEvent: false });
    this.replacementForm.enable({ emitEvent: false });
    this.updateQualificationLevelEnabledState();
    this.updateTrialPeriodEnability();
  }
}
